import { defineStore } from 'pinia'
import orgApi from '~/apis/orgApi'
import { type Org } from '~/constants/orgTypes'
import debounce from 'lodash.debounce'

const debouncedSave = debounce(
  async (org: Org): Promise<void> => {
    await orgApi.updateOrg(org)
  },
  1000,
  { maxWait: 5000 },
)

export const useOrgStore = defineStore('org', {
  state: () => ({
    selected: {} as Org,
    list: [] as Org[],
  }),
  getters: {},
  actions: {
    async getOrg() {
      const org = await orgApi.getOrg()
      this.$state.selected = org
      this.$state.list = [org]
      const { canViewIntakes, canViewAudits } = usePermissions()
      if (!canViewIntakes.value) usePositionStore().$reset()
      if (!canViewAudits.value) useAuditStore().$reset()
    },
    // flush=true performs the PATCH now and THROWS on failure — required for
    // explicit "Save" buttons so a failed save can't toast success. Without
    // flush, the write is debounced fire-and-forget (auto-save while typing);
    // `await debouncedSave(...)` resolves before the request even fires, so it
    // must never back a success message.
    async save(flush?: boolean): Promise<void> {
      if (!this.$state.selected.id) return
      if (flush) {
        debouncedSave.cancel()
        await orgApi.updateOrg(this.$state.selected)
        return
      }
      debouncedSave(this.$state.selected)
    },
    updateOrgField<K extends keyof Org>(orgField: K, value: Org[K]) {
      this.$state.selected[orgField] = value
    },
  },
})
